// Клиентская маршрутизация - фасад над движком (utils/routingEngine.ts). Если в
// браузере есть Web Worker, всё тяжёлое (разбор тайлов, склейка графа, индекс,
// Dijkstra) идёт в воркере и не блокирует карту; иначе (или если воркер упал)
// тот же движок работает в основном потоке с axios-загрузчиком тайлов.
import api, { API_BASE_URL } from "../api";
import * as engine from "../utils/routingEngine";
import { createIdbTileStore, createMemoryTileStore } from "../utils/routingTileStore";
import { RoutingWorkerClient, WorkerFailure, type WorkerLike } from "../utils/routingWorkerClient";
import type { WorkerOp } from "../utils/routingWorkerProtocol";
import type { Coordinate, RoutedPath } from "./useRoutingPath";
import type { RoutingStat } from "../utils/routingStats";

engine.setTileFetcher(async ({ params, headers }) => {
  const res = await api.get<ArrayBuffer>("/routing/graph", {
    params,
    responseType: "arraybuffer",
    headers,
    validateStatus: (s: number) => s === 200 || s === 304,
  });
  return { status: res.status, data: res.data, etag: String((res.headers as Record<string, unknown>)?.etag ?? "") };
});

type WorkerFactory = () => WorkerLike;
let factoryOverride: WorkerFactory | null | undefined; // undefined - по умолчанию, null - без воркера
let client: RoutingWorkerClient | null | undefined; // undefined - ещё не создавали

// Для тестов: подменить фабрику воркера (null - работать только во встроенном движке).
export function setWorkerFactoryForTests(f: WorkerFactory | null): void {
  factoryOverride = f;
  client = undefined;
}

const defaultFactory = (): WorkerFactory | null => {
  if (import.meta.env.MODE === "test" || typeof Worker === "undefined") return null;
  return () => new Worker(new URL("../workers/routing.worker.ts", import.meta.url), { type: "module" }) as unknown as WorkerLike;
};

const getClient = (): RoutingWorkerClient | null => {
  if (client !== undefined) return client;
  const factory = factoryOverride === undefined ? defaultFactory() : factoryOverride;
  try {
    const base = typeof window !== "undefined" ? new URL(API_BASE_URL, window.location.href).href : API_BASE_URL;
    client = factory ? new RoutingWorkerClient(factory, base) : null;
  } catch {
    client = null;
  }
  return client;
};

async function viaWorker<T>(op: WorkerOp, payload: Record<string, unknown>, inline: () => Promise<T>): Promise<T> {
  const c = getClient();
  if (c) {
    try {
      return await c.call<T>(op, payload);
    } catch (e) {
      if (e instanceof WorkerFailure) client = null; // воркер не работает - дальше только встроенный движок
    }
  }
  return inline();
}

const quiet = (op: WorkerOp, payload: Record<string, unknown> = {}) => {
  const c = getClient();
  if (c) c.call(op, payload).catch(() => {});
};

export function routeClient(
  start: Coordinate,
  destination: Coordinate,
  opts: { destinationMode?: "network" | "poi"; snapRadiusM?: number } = {}
): Promise<{ result: RoutedPath; stat: RoutingStat } | null> {
  return viaWorker("route", { start, destination, opts }, () => engine.routeClient(start, destination, opts));
}

export function warmRoutingAround(lon: number, lat: number): Promise<void> {
  return viaWorker<null>("warm", { lon, lat }, () => engine.warmRoutingAround(lon, lat).then(() => null)).then(() => undefined);
}

export function prefetchRoutingAround(lon: number, lat: number, ring = 1): Promise<void> {
  return viaWorker<null>("prefetch", { lon, lat, ring }, () => engine.prefetchRoutingAround(lon, lat, ring).then(() => null)).then(() => undefined);
}

export function clearClientRoutingCache(): void {
  engine.clearClientRoutingCache();
  quiet("clear");
}

export function setWarmAhead(on: boolean): void {
  engine.setWarmAhead(on);
  quiet("setWarmAhead", { on });
}

// Хранилище тайлов: memory - пустое (холодный старт), idb - IndexedDB. Нужно замерам.
export function setRoutingStoreKind(kind: "memory" | "idb"): void {
  engine.setTileStore(kind === "idb" ? createIdbTileStore() : createMemoryTileStore());
  quiet("setStore", { kind });
}

export const setTileStore = engine.setTileStore;
